import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Leaf, Globe, Recycle, TrendingUp, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SustainabilityPage = () => {
  const navigate = useNavigate();

  const principles = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Environmental Protection',
      description: 'Preserving natural resources and ecosystems for future generations through responsible practices and conservation efforts.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Social Responsibility',
      description: 'Ensuring fair treatment, equal opportunities, and improving quality of life for all stakeholders in society.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Economic Viability',
      description: 'Creating long-term economic value while maintaining environmental and social responsibility.'
    }
  ];

  const actions = [
    {
      icon: <Recycle className="w-6 h-6" />,
      title: 'Circular Economy',
      description: 'Transitioning from linear "take-make-dispose" models to circular systems that eliminate waste and regenerate natural systems.'
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      title: 'Green Technology',
      description: 'Investing in renewable energy, clean technologies, and sustainable innovations that reduce environmental impact.'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Measurable Goals',
      description: 'Setting clear, achievable targets aligned with UN Sustainable Development Goals and monitoring progress regularly.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <Leaf className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Sustainable Development
            </h1>
          </div>
          
          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            Understanding and implementing sustainable practices for a better future
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Definition */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Sustainable Development?
            </h2>
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Sustainable development is a common agenda for global concern, which everybody agrees upon, 
                but bringing this global concern into public policies is a difficult task.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-semibold">
                According to the Brundtland Report, sustainable development is defined as:
              </p>
              <p className="text-xl text-[#0d6571] font-bold mt-4 italic">
                "To meet the needs of present without compromising the ability of future generations 
                to meet their own needs."
              </p>
            </div>
          </div>

          {/* Three Pillars */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              The Three Pillars of Sustainability
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {principles.map((principle, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#0d6571] hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-[#d97841] mb-4">{principle.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{principle.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Actions */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Key Actions for Sustainable Development
            </h2>
            <div className="space-y-6">
              {actions.map((action, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-white to-[#0d6571]/5 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
                >
                  <div className="bg-[#0d6571] text-white p-3 rounded-lg flex-shrink-0">
                    {action.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{action.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{action.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-[#0d6571] rounded-2xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why Sustainable Development Matters
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Global Challenges</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Climate change and environmental degradation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Resource depletion and biodiversity loss</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Social inequality and economic instability</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Growing population and urbanization</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Business Benefits</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Enhanced brand reputation and customer loyalty</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Cost savings through resource efficiency</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Access to new markets and investment opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Regulatory compliance and risk reduction</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SustainabilityPage;